import { AlertTriangle, ChevronDown, ExternalLink, Info, Loader2, RefreshCw, Search, ShieldAlert, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Pagination from '../components/Pagination';
import { safetyPayoutService } from '../services/api';
import './SafetyPayouts.css';

const FILTERS = [
  ['TO_MANAGE', 'Da gestire'],
  ['NEXT_10_DAYS', 'Prossimi 10 giorni'],
  ['OVERDUE', 'Scaduti'],
  ['PENDING', 'Pending'],
  ['FAILED', 'Failed'],
  ['ALL', 'Tutti'],
];

const ACTION_LABELS = {
  INSTANT_WITH_FEE: 'Instant Payout',
  STANDARD_ALREADY_RECOVERED: 'Standard Payout',
  STANDARD_LOW_BALANCE: 'Standard Payout',
  STANDARD_RESIDUAL: 'Standard Payout',
  NONE: 'Nessuna operazione',
};

const STATUS_LABELS = {
  NONE: 'Nessuno',
  DUE: 'Da gestire',
  PENDING: 'Pending',
  FAILED: 'Failed',
};

function money(cents) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(cents || 0) / 100);
}

function date(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('it-IT');
}

function daysLabel(row) {
  if (row.daysRemaining == null) return '—';
  if (row.daysRemaining < 0) return `${Math.abs(row.daysRemaining)} giorni oltre`;
  return `${row.daysRemaining} giorni`;
}

function actionReason(row) {
  return row.reason || 'Nessuna operazione necessaria.';
}

function feeLabel(fee) {
  if (!fee) return 'Commissione';
  return `Commissione (${Number(fee.percentage || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}% + ${money(fee.fixedCents)})`;
}

function StatusBadge({ status }) {
  return <span className={`sp-status sp-status-${String(status || 'NONE').toLowerCase()}`}>{STATUS_LABELS[status] || status}</span>;
}

function ActionBadge({ action }) {
  const label = ACTION_LABELS[action] || action;
  return <span className={`sp-action sp-action-${action === 'INSTANT_WITH_FEE' ? 'instant' : action === 'NONE' ? 'none' : 'standard'}`}>{label}</span>;
}

function InfoTip({ children }) {
  return <span className="sp-info" title={children} aria-label={children}><Info size={13} /></span>;
}

export default function SafetyPayouts() {
  const [filter, setFilter] = useState('TO_MANAGE');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState({ term: '', submission: 0 });
  const [tenantId, setTenantId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [filterOptions, setFilterOptions] = useState({ tenants: [], venues: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 25, totalPages: 1 });
  const [rows, setRows] = useState([]);
  const [feeSettings, setFeeSettings] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [menuFor, setMenuFor] = useState(null);
  const [modal, setModal] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [message, setMessage] = useState(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async (isRefresh = false) => {
    const requestId = ++requestIdRef.current;
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError('');
      const response = isRefresh
        ? await safetyPayoutService.refresh({ filter, search: submittedSearch.term, tenantId, venueId, page, pageSize })
        : await safetyPayoutService.getCandidates({ filter, search: submittedSearch.term, tenantId, venueId, page, pageSize });
      if (requestId !== requestIdRef.current) return;
      setRows(response.rows || []);
      setFilterOptions(response.filterOptions || { tenants: [], venues: [] });
      const nextPagination = response.pagination || { total: response.rows?.length || 0, page: 1, pageSize, totalPages: 1 };
      setPagination(nextPagination);
      if (nextPagination.page !== page) setPage(nextPagination.page);
      setFeeSettings(response.feeSettings || null);
      setRefreshedAt(response.refreshedAt || new Date().toISOString());
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err.response?.data?.error || err.message || 'Errore caricamento Safety Payouts');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [filter, submittedSearch, tenantId, venueId, page, pageSize]);

  useEffect(() => { load(false); }, [load]);

  const changeFilter = (nextFilter) => {
    setPage(1);
    setFilter(nextFilter);
  };

  const changeSearch = (event) => {
    setSearch(event.target.value);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSubmittedSearch((previous) => ({
      term: search.trim(),
      submission: previous.submission + 1,
    }));
  };

  const changePageSize = (nextPageSize) => {
    setPage(1);
    setPageSize(nextPageSize);
  };

  const changeTenant = (event) => {
    setPage(1);
    setTenantId(event.target.value);
    setVenueId('');
  };

  const changeVenue = (event) => {
    setPage(1);
    setVenueId(event.target.value);
  };

  const availableVenues = tenantId
    ? filterOptions.venues.filter((venue) => venue.tenantId === tenantId)
    : filterOptions.venues;

  const openModal = (row, action) => {
    setMenuFor(null);
    setModal({
      row,
      action,
      requestId: window.crypto?.randomUUID?.() || `safety_${Date.now()}`,
    });
  };

  const execute = async () => {
    if (!modal) return;
    try {
      setExecuting(true);
      setMessage(null);
      await safetyPayoutService.execute(modal.row.workerPaymentProfileId, modal.action, modal.requestId);
      setModal(null);
      setMessage({ type: 'success', text: 'Payout eseguito correttamente. Il saldo e lo stato del ciclo sono stati aggiornati.' });
      await load(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Payout non eseguito' });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="sp-page">
      <header className="sp-header">
        <div>
          <h1><ShieldAlert size={22} /> Safety Payouts</h1>
          <p>Verifica manuale dei cicli di sicurezza dei Connected Account.</p>
        </div>
        <button className="sp-button sp-button-primary" onClick={() => load(true)} disabled={refreshing || loading}>
          {refreshing ? <Loader2 className="sp-spin" size={16} /> : <RefreshCw size={16} />}
          Aggiorna
        </button>
      </header>

      <section className="sp-info-panel">
        <details>
          <summary>Come funziona la sezione Safety Payouts?</summary>
          <div className="sp-info-content">
            <h3>Obiettivo e scadenza</h3>
            <p>I Connected Account sono configurati con payout manuali. Il riferimento operativo adottato è il limite Stripe di 90 giorni; SoonTip fissa una <strong>Safety Date prudenziale a 80 giorni</strong> per intervenire con anticipo.</p>

            <h3>Ciclo di sicurezza</h3>
            <ul>
              <li>Il ciclo si apre con la prima mancia accreditata e le mance successive non spostano la Safety Date.</li>
              <li>Un payout del worker o dell'Admin non chiude il ciclo se restano fondi disponibili, istantanei o in elaborazione.</li>
              <li>Il ciclo si chiude solo quando non restano fondi da regolarizzare. Una nuova mancia aprirà un nuovo ciclo e una nuova Safety Date.</li>
            </ul>

            <h3>Come viene scelta l'azione suggerita</h3>
            <ul>
              <li><strong>Costo Connect del mese non recuperato:</strong> se Stripe consente il trasferimento istantaneo e restituisce un netto prelevabile, viene suggerito Instant Payout con la fee effettiva del periodo.</li>
              <li><strong>Costo Connect del mese già recuperato:</strong> viene suggerito Standard Payout senza una nuova commissione Instant.</li>
              <li><strong>Instant non disponibile:</strong> se esiste saldo Standard disponibile, viene suggerito Standard Payout per rispettare la scadenza.</li>
              <li><strong>Fondi non ancora trasferibili:</strong> se sono ancora in elaborazione o il saldo disponibile è inferiore al minimo Stripe di €1,00, non viene suggerita alcuna operazione. Il ciclo resta Pending fino alla disponibilità di un importo sufficiente.</li>
            </ul>

            <h3>Commissione, netto massimo e residuo</h3>
            <p>Stripe restituisce sia il saldo istantaneo lordo sia il <strong>netto massimo trasferibile</strong> per la destinazione selezionata. SoonTip usa sempre questo netto come importo del bonifico e non tenta di ricalcolarlo.</p>
            <ul>
              <li><strong>Commissione prevista:</strong> quota fissa più la percentuale applicata all'importo effettivo del bonifico, con arrotondamento ai centesimi.</li>
              <li><strong>Residuo:</strong> differenza tra saldo lordo, netto trasferito e commissione prevista. Non è una commissione e rimane sul Connected Account per un payout successivo.</li>
              <li><strong>Esempio:</strong> con lordo €12,80, netto massimo Stripe €10,54 e tariffa 1,25% + €2,10, la commissione prevista è €2,23 e il residuo è €0,03.</li>
            </ul>

            <h3>Regola mensile e prelievi del worker</h3>
            <p>Ogni Instant Payout volontario richiesto dal worker può avere la propria commissione. Quando va a buon fine, il costo Connect del mese viene segnato come recuperato: un Safety Payout nello <strong>stesso mese</strong> viene quindi suggerito come Standard senza nuova fee. Se il ciclo Pending prosegue nel <strong>mese successivo</strong>, il costo mensile viene rivalutato e può essere suggerito un nuovo Instant Payout con la fee del nuovo mese.</p>

            <h3>Scenari pratici</h3>
            <ol>
              <li><strong>Prima mancia e apertura del ciclo.</strong> Il worker riceve la prima mancia il 1° settembre. Il ciclo parte in quella data e la Safety Date viene fissata 80 giorni dopo. Le mance ricevute durante il ciclo aumentano il saldo, ma non spostano la scadenza.</li>
              <li><strong>Prelievo volontario completo.</strong> Il worker richiede un Instant Payout dalla propria dashboard. La commissione viene applicata a quel prelievo e il costo Connect del mese viene considerato recuperato. Se non restano fondi disponibili o in elaborazione, il ciclo si chiude; la prima mancia successiva ne aprirà uno nuovo.</li>
              <li><strong>Prelievo volontario con residuo nello stesso mese.</strong> Dopo l'Instant del worker restano fondi Pending. Quando diventano disponibili nello stesso mese, la pagina suggerisce uno Standard Payout senza una nuova commissione Instant. Il ciclo resta Pending finché tutti i fondi non sono stati regolati.</li>
              <li><strong>Prelievo a ridosso della fine del mese.</strong> Il worker effettua un Instant il 29 settembre e restano fondi Pending. Se diventano disponibili il 30 settembre, il costo Connect di settembre è già recuperato e viene suggerito Standard. Se diventano disponibili il 2 ottobre, il nuovo costo mensile non è ancora recuperato: quando Stripe consente Instant e l'importo è sufficiente, viene suggerito Instant con la fee di ottobre.</li>
              <li><strong>Instant non disponibile alla Safety Date.</strong> Il costo mensile non è ancora recuperato, ma il conto o la destinazione non consentono Instant. Se il saldo Standard è almeno €1,00, viene suggerito Standard per non superare la scadenza; in questo caso non viene applicata la fee Instant.</li>
              <li><strong>Residuo inferiore al minimo Stripe.</strong> Restano, per esempio, €0,03. Stripe non consente payout EUR inferiori a €1,00: non viene proposta alcuna operazione e il ciclo resta Pending. Dopo nuove mance, appena il saldo trasferibile raggiunge almeno €1,00, l'azione viene rivalutata secondo il mese e il costo Connect.</li>
            </ol>

            <h3>Uso operativo della pagina</h3>
            <ul>
              <li><strong>Aggiorna</strong> rilegge saldi, compatibilità e destinazioni direttamente da Stripe; un account non accessibile viene marcato Failed senza bloccare gli altri.</li>
              <li><strong>Available</strong> è il saldo usabile per Standard Payout; <strong>Instant Payout netto</strong> è il massimo netto trasferibile subito; <strong>In elaborazione</strong> non è ancora prelevabile.</li>
              <li>L'azione suggerita non parte automaticamente. L'Admin deve aprire la conferma e autorizzare il payout; il saldo viene riletto da Stripe in quel momento.</li>
              <li>La colonna Fee / Netto mostra la commissione prevista sull'importo del bonifico, il netto massimo restituito da Stripe e l'eventuale residuo. La nota blu mostra la configurazione corrente dei Global Settings.</li>
            </ul>
            <a href="https://docs.stripe.com/connect/manual-payouts" target="_blank" rel="noreferrer">Documentazione Stripe sui Manual Payouts <ExternalLink size={13} /></a>
          </div>
        </details>
      </section>

      <div className="sp-toolbar">
        <div className="sp-filter-tabs">
          {FILTERS.map(([value, label]) => (
            <button key={value} className={filter === value ? 'active' : ''} onClick={() => changeFilter(value)}>{label}</button>
          ))}
        </div>
        <div className="sp-organization-filters">
          <label>
            <span>Tenant</span>
            <select value={tenantId} onChange={changeTenant}>
              <option value="">Tutti i tenant</option>
              {filterOptions.tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Locale</span>
            <select value={venueId} onChange={changeVenue}>
              <option value="">Tutti i locali</option>
              {availableVenues.map((venue) => (
                <option key={venue.id} value={venue.id}>{venue.name}</option>
              ))}
            </select>
          </label>
          <form className="sp-search" onSubmit={submitSearch}>
            <input
              value={search}
              onChange={changeSearch}
              placeholder="Nome, email, tenant, locale o Connected Account"
              aria-label="Cerca Safety Payout"
            />
            <button type="submit"><Search size={15} /> Cerca</button>
          </form>
        </div>
      </div>

      {feeSettings && <div className="sp-fee-note"><Wallet size={15} /> Fee corrente: <strong>{feeSettings.percentage}% + {money(feeSettings.fixedCents)}</strong> <InfoTip>Letta dai Global Settings e usata per la raccomandazione Instant Payout.</InfoTip></div>}
      {message && <div className={`sp-message sp-message-${message.type}`}>{message.type === 'error' ? <AlertTriangle size={16} /> : <ShieldAlert size={16} />}{message.text}</div>}
      {error && <div className="sp-message sp-message-error"><AlertTriangle size={16} />{error}</div>}

      <div className="sp-meta">{pagination.total} account · pagina {pagination.page} di {pagination.totalPages} · aggiornato {refreshedAt ? new Date(refreshedAt).toLocaleTimeString('it-IT') : '—'}</div>
      <div className="sp-table-wrap">
        {loading ? <div className="sp-loading"><Loader2 className="sp-spin" /> Caricamento...</div> : (
          <table className="sp-table">
            <thead><tr>
              <th>Worker</th><th>Tenant / Locale</th><th>Safety Date</th><th><span>Available (Standard Payout) <InfoTip>Saldo Stripe disponibile per Standard Payout.</InfoTip></span></th><th><span>Instant Payout netto <InfoTip>Importo netto che Stripe rende effettivamente prelevabile con Instant Payout.</InfoTip></span></th><th>Connect Cost</th><th>Azione suggerita</th><th>Fee / Netto</th><th>Status</th><th>Azioni</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan="10" className="sp-empty">Nessun Safety Payout da gestire.</td></tr> : rows.map((row) => {
                const alternative = row.action === 'INSTANT_WITH_FEE' ? 'STANDARD_LOW_BALANCE' : row.payoutOptions.instantAllowed ? 'INSTANT_WITH_FEE' : null;
                return <tr key={row.workerPaymentProfileId}>
                  <td><strong>{row.workerName}</strong><small>{row.workerEmail || '—'}</small><code>{row.providerAccountId}</code></td>
                  <td>
                    {row.affiliations?.length ? row.affiliations.map((affiliation) => (
                      <span className="sp-affiliation" key={affiliation.workerId}>
                        <strong>{affiliation.tenantName || 'Tenant non disponibile'}</strong>
                        <small>{affiliation.venueName || 'Locale non disponibile'}</small>
                      </span>
                    )) : '—'}
                  </td>
                  <td><strong>{date(row.nextSafetySweepAt)}</strong><small>{daysLabel(row)}</small></td>
                  <td>{money(row.available)}{row.pending > 0 && <small>In elaborazione: {money(row.pending)}</small>}</td><td>{money(row.instantNetAvailable)}<small>lordo {money(row.instantAvailable)}</small></td>
                  <td>{row.connectCostRecovered ? <span className="sp-ok">Recuperato</span> : <span className="sp-warning">Non recuperato</span>}</td>
                  <td><ActionBadge action={row.action} /><small>{actionReason(row)}</small></td>
                  <td>{row.action === 'INSTANT_WITH_FEE' ? <><strong>{money(row.fee.calculatedAmountCents)}</strong><small>net {money(row.fee.netAmountCents)}</small>{row.fee.residualAmountCents > 0 && <small>residuo {money(row.fee.residualAmountCents)}</small>}</> : <><strong>€0,00</strong><small>{feeLabel(row.fee)}</small></>}</td>
                  <td><StatusBadge status={row.status} /></td>
                  <td className="sp-actions">
                    {row.action !== 'NONE' && <button className="sp-button sp-button-primary sp-button-small" onClick={() => openModal(row, row.action)}>Esegui {row.action === 'INSTANT_WITH_FEE' ? 'Instant' : 'Standard'}</button>}
                    {alternative && <div className="sp-menu"><button className="sp-more" onClick={() => setMenuFor(menuFor === row.workerPaymentProfileId ? null : row.workerPaymentProfileId)} aria-label="Altre azioni"><ChevronDown size={16} /></button>{menuFor === row.workerPaymentProfileId && <div className="sp-menu-pop"><button onClick={() => openModal(row, alternative)}>Esegui {alternative === 'INSTANT_WITH_FEE' ? 'Instant Payout' : 'Standard Payout'}</button></div>}</div>}
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        )}
      </div>

      {pagination.total > 0 && <Pagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemsPerPage={pagination.pageSize}
        onPageChange={setPage}
        onItemsPerPageChange={changePageSize}
      />}

      {modal && <div className="sp-modal-backdrop" onClick={() => !executing && setModal(null)}><div className="sp-modal" onClick={(event) => event.stopPropagation()}>
        <div className="sp-modal-header"><div><h2>Conferma {modal.action === 'INSTANT_WITH_FEE' ? 'Instant Payout' : 'Standard Payout'}</h2><p>{modal.row.workerName} · {modal.row.tenantNames?.join(', ') || 'Tenant non disponibile'} · {modal.row.venueNames?.join(', ') || 'Locale non disponibile'} · {modal.row.providerAccountId}</p></div><button onClick={() => setModal(null)} disabled={executing}><X size={18} /></button></div>
        <div className="sp-modal-body"><div className="sp-confirm-grid"><span>Available</span><strong>{money(modal.row.available)}</strong><span>Instant Payout netto</span><strong>{money(modal.row.instantNetAvailable)}</strong><span>Instant lordo</span><strong>{money(modal.row.instantAvailable)}</strong><span>Azione</span><strong>{ACTION_LABELS[modal.action]}</strong><span>{feeLabel(modal.row.fee)}</span><strong>{modal.action === 'INSTANT_WITH_FEE' ? money(modal.row.fee.calculatedAmountCents) : '€0,00'}</strong>{modal.action === 'INSTANT_WITH_FEE' && modal.row.fee.residualAmountCents > 0 && <><span>Residuo previsto</span><strong>{money(modal.row.fee.residualAmountCents)}</strong></>}<span>Importo netto previsto</span><strong>{modal.action === 'INSTANT_WITH_FEE' ? money(modal.row.fee.netAmountCents) : money(modal.row.available)}</strong></div><p className="sp-reason">{actionReason(modal.row)}</p><p className="sp-note">Il payout è manuale: il saldo viene riletto da Stripe al momento della conferma.</p></div>
        <div className="sp-modal-footer"><button className="sp-button sp-button-secondary" onClick={() => setModal(null)} disabled={executing}>Annulla</button><button className="sp-button sp-button-primary" onClick={execute} disabled={executing}>{executing ? <Loader2 className="sp-spin" size={15} /> : null} Conferma payout</button></div>
      </div></div>}
    </div>
  );
}
